import React, { useState } from "react";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { Contacts } from "@/src/gql/graphql";
import type { Client, Contact } from "@/lib/types/database";

// Context hooks
import { useDetailActionContextOptional } from "@/contexts/DetailActionContext";

// Sub-components
import { ClientInfoDisplay } from "./ClientInfoDisplay";
import { ContactTimeline } from "./ContactTimeline";
import { ContactTasks } from "./ContactTasks";
import { ContactTickets } from "./ContactTickets";
import { ContactDeals } from "./ContactDeals";
import { ContactCompanyTab } from "./ContactCompanyTab";
import { ContactDocumentation } from "./ContactDocumentation";
import { ClientDriveTab } from "./ClientDetails/ClientDriveTab";
import { ContactRecurringTransactionsTab } from "@/features/recurring-transactions/components/ContactRecurringTransactionsTab";
import { ContactContractsTab } from "@/features/contracts/components/tabs/ContactContractsTab";
import { ContactEmailsDisplay as ContactEmailsDisplayBase } from "@/features/contacts/components/ContactEmailsDisplay";
import { useRoleCheck } from "@/lib/hooks/auth/useRoleCheck";
import { AISummary } from "@/components/ai/ai-summary";
import { CONTACT_TIMELINE_SUMMARY_OPTIONS } from "@/lib/domain/crm-entity-relationships";
import { ClientDetailTab } from "./tabs/ClientDetailTab";
import { SequenceStatusBadge } from "@/components/sequences/SequenceStatusBadge";
import { ContactFirstTouch } from "@/components/marketing/analytics/ContactFirstTouch";
import { CollapsibleSection } from "@/components/shared/CollapsibleSection";
import { EntityAuditHistoryTab } from "@/components/audit/EntityAuditHistoryTab";

// Tab components kept in separate files for size compliance
import { ContactTimeEntriesDisplay } from "./tabs/ContactTimeEntriesTab";
import { ContactQuickBooksDisplay } from "./tabs/ContactQuickBooksTab";
import { ContactSequenceDisplay } from "./tabs/ContactSequenceTab";

// Re-export so callers can import directly from this module
export { ContactTimeEntriesDisplay } from "./tabs/ContactTimeEntriesTab";
export { ContactQuickBooksDisplay } from "./tabs/ContactQuickBooksTab";

// ============================================================================
// ContactDetailsDisplay
// ============================================================================

export const ContactDetailsDisplay: React.FC<TabContentProps<Contacts>> = ({
  item: contact,
}) => {
  // Contacts and Client resolve to the same underlying type
  const client: Client = contact;

  return (
    <div className="min-h-[400px] space-y-4 md:space-y-6">
      {contact.id ? (
        <div className="flex">
          <SequenceStatusBadge contactId={contact.id} />
        </div>
      ) : null}
      {/* "How did this person find us" — first-touch marketing attribution. Renders
          nothing when there is none (most contacts), so it costs no space on records
          that predate the analytics rollout. */}
      {contact.id ? <ContactFirstTouch contactId={contact.id} /> : null}
      <ClientInfoDisplay
        client={client}
        sections={["contact"]}
        variant="default"
        showTitles={true}
        className="grid-cols-1"
      />
      {contact.id ? (
        <AISummary
          contactId={contact.id}
          summaryType="timeline"
          options={{ ...CONTACT_TIMELINE_SUMMARY_OPTIONS }}
          showMetadata={true}
          showRefreshButton={true}
          className="mb-0"
        />
      ) : null}
      {/* Recency-first: a salesperson opening a contact wants the last touch
          before the mailing address. Activity sits directly under the AI
          summary; logistics (contract/address/other) follow. */}
      <div>
        <h3 className="text-base md:text-lg font-semibold text-card-foreground mb-3">Activity</h3>
        <ContactTimeline email={contact.email} client={contact} showAISummary={false} />
      </div>
      <ClientInfoDisplay
        client={client}
        sections={["contract", "address"]}
        variant="default"
        showTitles={true}
        className="grid-cols-1"
      />
      <CollapsibleSection title="Other Information" defaultCollapsed>
        <ClientInfoDisplay
          client={client}
          sections={["other"]}
          variant="default"
          showTitles={false}
          className="grid-cols-1"
        />
      </CollapsibleSection>
    </div>
  );
};

// ============================================================================
// ContactEmailsDisplay
// ============================================================================

export const ContactEmailsDisplay: React.FC<TabContentProps<Contacts>> = ({
  item: contact,
  onRefresh,
}) => (
  <ContactEmailsDisplayBase
    contact={contact}
    onUpdate={onRefresh ? () => onRefresh() : undefined}
  />
);

// ============================================================================
// ContactTimelineDisplay
// ============================================================================

export const ContactTimelineDisplay: React.FC<TabContentProps<Contacts>> = ({
  item: contact,
}) => (
  <ClientDetailTab contentClassName="min-h-[400px]">
    <ContactTimeline email={contact.email} client={contact} />
  </ClientDetailTab>
);

// ============================================================================
// ContactTasksDisplay
// ============================================================================

export const ContactTasksDisplay: React.FC<TabContentProps<Contacts>> = ({
  item: contact,
}) => (
  <ClientDetailTab className="mt-4">
    <ContactTasks client={contact} />
  </ClientDetailTab>
);

// ============================================================================
// ContactTicketsDisplay
// ============================================================================

export const ContactTicketsDisplay: React.FC<TabContentProps<Contacts>> = ({
  item: contact,
}) => (
  <ClientDetailTab className="mt-4">
    <ContactTickets client={contact} />
  </ClientDetailTab>
);

// ============================================================================
// ContactRecurringTransactionsDisplay
// ============================================================================

export const ContactRecurringTransactionsDisplay: React.FC<TabContentProps<Contacts>> = ({
  item: contact,
}) => <ContactRecurringTransactionsTab customerId={contact.id} />;

// ============================================================================
// ContactCompanyDisplay
// ============================================================================

export const ContactCompanyDisplay: React.FC<TabContentProps<Contacts>> = ({
  item: contact,
  onRefresh,
}) => {
  const actionContext = useDetailActionContextOptional();
  const contextRefresh = actionContext?.onRefresh;
  const refreshCallback = onRefresh || contextRefresh;

  const [currentContact, setCurrentContact] = useState(contact);

  React.useEffect(() => {
    setCurrentContact(contact);
  }, [contact]);

  return (
    <ClientDetailTab className="mt-4">
      <ContactCompanyTab
        contact={currentContact}
        onRefresh={refreshCallback}
        onCompanyAdded={(companyName) => {
          setCurrentContact((prev) => ({ ...prev, company_name: companyName }));
          void refreshCallback?.();
        }}
      />
    </ClientDetailTab>
  );
};

// ============================================================================
// ContactDriveDisplay
// ============================================================================

export const ContactDriveDisplay: React.FC<TabContentProps<Contacts>> = ({
  item: contact,
}) => <ClientDriveTab client={contact} />;

// ============================================================================
// ContactDocumentationDisplay
// ============================================================================

export const ContactDocumentationDisplay: React.FC<TabContentProps<Contacts>> = ({
  item: contact,
}) => {
  const { isAdmin, isEmployee } = useRoleCheck();

  if (!isAdmin && !isEmployee) {
    return (
      <div className="flex items-center justify-center py-10 md:py-12 px-4 md:px-0">
        <div className="text-center">
          <h3 className="text-base md:text-lg font-semibold text-card-foreground mb-2">Access Restricted</h3>
          <p className="text-gray-600 dark:text-gray-400">
            You need admin or employee privileges to view documentation.
          </p>
        </div>
      </div>
    );
  }

  return <ContactDocumentation client={contact} />;
};

// ============================================================================
// ContactDealsDisplay
// ============================================================================

export const ContactDealsDisplay: React.FC<TabContentProps<Contacts>> = ({
  item: contact,
}) => {
  // Contacts and Contact resolve to the same underlying type
  const asContact: Contact = contact;

  if (!contact.company_id) {
    return (
      <div className="mt-4 p-4 md:p-6 border rounded-lg bg-card-50">
        <h3 className="text-base md:text-lg font-semibold text-card-foreground mb-2">Deals</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Link a company to this contact to see and manage deals. Use the Company tab to add or
          select a company.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <ContactDeals client={asContact} />
    </div>
  );
};

// ============================================================================
// ContactContractsDisplay
// ============================================================================

export const ContactContractsDisplay: React.FC<TabContentProps<Contacts>> = ({
  item: contact,
}) => (
  <ContactContractsTab contactId={contact.id} contactName={contact.name ?? undefined} />
);

// ============================================================================
// ContactHistoryDisplay
// ============================================================================
// The audit feed's SECOND entry point (the first is the "View history & audit"
// place-modal). Both render `EntityAuditFeed` — see EntityAuditHistoryTab for
// why the empty-parent guard lives at the tab boundary.

export const ContactHistoryDisplay: React.FC<TabContentProps<Contacts>> = ({
  item: contact,
}) => (
  <EntityAuditHistoryTab
    entityType="contacts"
    entityId={contact.id}
    subjectLabel={contact.name ?? "Contact"}
    entityNoun="contact"
  />
);

// ============================================================================
// Namespace export — keeps existing import sites working
// ============================================================================

export const ContactDetailTabComponents = {
  ContactDetailsDisplay,
  ContactEmailsDisplay,
  ContactTimelineDisplay,
  ContactTasksDisplay,
  ContactTicketsDisplay,
  ContactCompanyDisplay,
  ContactDriveDisplay,
  ContactQuickBooksDisplay,
  ContactDocumentationDisplay,
  ContactTimeEntriesDisplay,
  ContactRecurringTransactionsDisplay,
  ContactDealsDisplay,
  ContactContractsDisplay,
  ContactSequenceDisplay,
  ContactHistoryDisplay,
};
